import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Button, Dropdown, Modal, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EmptyState } from "@/shared/components/EmptyState";
import type { StatusTone } from "@/shared/components/StatusTag";
import { StatusTag } from "@/shared/components/StatusTag";
import { TableToolbar } from "@/shared/components/TableToolbar";
import { tablePagination } from "@/shared/utils/tablePagination";
import { useDeleteDevice, useDevices } from "../hooks/useDevices";
import { useDevicesStore } from "../store/devices.store";
import type { Device } from "../types";

interface DeviceTableProps {
	organizationId?: string;
	organizationName?: string;
	page: number;
	limit: number;
	onPageChange: (page: number) => void;
}

const DIRECTION_LABELS: Record<string, string> = {
	IN: "Kirish",
	OUT: "Chiqish",
	BOTH: "Ikkalasi",
};

const STATUS_COLORS: Record<string, StatusTone> = {
	online: "success",
	offline: "default",
	error: "error",
};

export function DeviceTable({
	organizationId,
	organizationName,
	page,
	limit,
	onPageChange,
}: DeviceTableProps) {
	const { data, isLoading } = useDevices({ organizationId, page, limit });
	const { mutate: deleteDevice } = useDeleteDevice();
	const openEditModal = useDevicesStore((s) => s.openEditModal);

	const columns: ColumnsType<Device> = [
		...(organizationName
			? [
					{
						title: "Tashkilot",
						key: "organization",
						width: 140,
						render: () => organizationName,
					},
				]
			: []),
		{
			title: "Nomi",
			dataIndex: "name",
			key: "name",
			ellipsis: true,
			render: (name: string, record: Device) => (
				<Space direction="vertical" size={0}>
					<span className="font-medium">{name}</span>
					<span className="text-xs text-ink-soft font-mono">{record.serialNumber}</span>
				</Space>
			),
		},
		{
			title: "IP",
			key: "ip",
			width: 120,
			render: (_: unknown, record: Device) => (
				<span className="font-mono">{`${record.ipAddress}:${record.port}`}</span>
			),
		},
		{
			title: "Yo'nalish",
			dataIndex: "direction",
			key: "direction",
			width: 100,
			render: (direction: string) => DIRECTION_LABELS[direction] ?? direction,
		},
		{
			title: "Joy",
			dataIndex: "locationDescription",
			key: "locationDescription",
			ellipsis: true,
			render: (v: string | null) => v ?? "—",
		},
		{
			title: "Holat",
			dataIndex: "status",
			key: "status",
			width: 100,
			render: (status: string) => (
				<StatusTag tone={STATUS_COLORS[status] ?? "default"}>{status}</StatusTag>
			),
		},
		{
			title: "Faol",
			dataIndex: "isActive",
			key: "isActive",
			width: 80,
			render: (isActive: boolean) => (
				<StatusTag tone={isActive ? "success" : "error"}>{isActive ? "Ha" : "Yo'q"}</StatusTag>
			),
		},
		{
			title: "Yaratilgan",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 150,
			render: (date: string) =>
				new Date(date).toLocaleDateString("uz-UZ", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
				}),
		},
		{
			title: "Amallar",
			key: "actions",
			width: 80,
			align: "center",
			render: (_: unknown, record: Device) => {
				const handleDelete = () => {
					Modal.confirm({
						title: "O'chirish",
						content: "Rostdan ham o'chirmoqchimisiz?",
						okText: "Ha",
						cancelText: "Yo'q",
						okButtonProps: { danger: true },
						onOk: () => deleteDevice(record.id),
					});
				};

				const menuItems: MenuProps["items"] = [
					{
						key: "edit",
						icon: <EditOutlined />,
						label: "Tahrirlash",
						onClick: () => openEditModal(record),
					},
					{
						key: "delete",
						icon: <DeleteOutlined />,
						label: "O'chirish",
						danger: true,
						onClick: handleDelete,
					},
				];

				return (
					<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
						<Button type="text" icon={<MoreOutlined />} size="small" />
					</Dropdown>
				);
			},
		},
	];

	return (
		<div>
			{organizationName && (
				<TableToolbar
					filters={
						<>
							<span className="text-ink-soft">Tashkilot:</span>
							<Tag>{organizationName}</Tag>
						</>
					}
				/>
			)}
			<Table
				columns={columns}
				dataSource={data?.data?.items}
				loading={isLoading}
				rowKey="id"
				pagination={tablePagination({
					page,
					limit,
					total: data?.data?.meta?.total,
					onChange: onPageChange,
					sizeChanger: false,
				})}
				size="middle"
				locale={{
					emptyText: (
						<EmptyState
							description={
								organizationName ? (
									<span>
										Qurilmalar yo'q — <strong>{organizationName}</strong>
									</span>
								) : (
									"Qurilmalar yo'q"
								)
							}
						/>
					),
				}}
			/>
		</div>
	);
}
